import {
    ItemType,
    NextcloudRequestConfig,
    ShareType,
    nextcloudApi,
} from '@sendent/nextcloud-api';
import { SHARE_REQUEST_CHUNK_SIZE } from '../helpers/constants';
import { logger } from '../helpers/logger';
import { mapInChunks } from '../helpers/utils';
import { ResolvedChatMember } from '../store/reducers/conversation/reducer';
import { ChatMember } from './microsoft/msGraphApi';

// Resolves all members with one Sendent app bulk lookup (app >= 4.4.0)
const resolveMembersViaBulkLookup = async (
    requestConfig: NextcloudRequestConfig,
    members: ChatMember[],
): Promise<ResolvedChatMember[] | null> => {
    // The server keys the result by the trimmed input, look up with the same key
    const emails = members.map((member) => member.email.trim());

    const lookupRes = await nextcloudApi.resolveUsersByEmail({
        requestConfig,
        emails,
    });
    if (!lookupRes.success) {
        return null;
    }

    return members.map((member, index) => ({
        userId: member.userId,
        email: member.email,
        displayName: member.displayName,
        ncShareWith: lookupRes.result[emails[index]]?.userId ?? null,
        resolveError: false,
    }));
};

// Resolves each member with its own sharee search, run in parallel chunks
const resolveMembersViaShareeSearch = (
    requestConfig: NextcloudRequestConfig,
    members: ChatMember[],
): Promise<ResolvedChatMember[]> =>
    mapInChunks(members, SHARE_REQUEST_CHUNK_SIZE, async (member) => {
        const shareesRes = await nextcloudApi.searchSharees({
            requestConfig,
            searchTerm: member.email,
            itemType: ItemType.Folder,
            shareTypes: [ShareType.User],
        });

        const ncShareWith = shareesRes.success
            ? (shareesRes.result.ocs.data.exact.users[0]?.value.shareWith ??
              null)
            : null;

        return {
            userId: member.userId,
            email: member.email,
            displayName: member.displayName,
            ncShareWith,
            resolveError: !shareesRes.success,
        };
    });

// Maps chat members to Nextcloud share targets, bulk lookup first
export const resolveChatMembers = async (
    requestConfig: NextcloudRequestConfig,
    members: ChatMember[],
): Promise<ResolvedChatMember[]> => {
    const bulkResolved = await resolveMembersViaBulkLookup(
        requestConfig,
        members,
    );
    if (bulkResolved) {
        return bulkResolved;
    }

    logger.info(
        'Bulk user lookup unavailable, resolving members one by one',
        'Share',
    );
    return resolveMembersViaShareeSearch(requestConfig, members);
};
